import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { STORAGE_KEYS } from '@shared/constants/storage-keys';
import { logger } from '@shared/utils/logger';

/**
 * The ACTIVE ENTITY — which of the group's companies the user is currently
 * operating as.
 *
 * ## Why this exists, and what it is not
 *
 * The app's authorization model is multi-company ACCESS: `user_companies` says
 * which companies an account may reach, and directory screens like Employees
 * and Clients are cross-company lists with an optional `company_id` filter.
 * That model is unchanged and this store does not replace it.
 *
 * Invoicing needs something the directories do not: a company that PERSISTS
 * across screens. A bill number sequence, an item catalogue, a tax schedule and
 * a template all belong to one company — the backend routes are literally
 * `/companies/:companyId/…` — and an invoice is issued BY a specific legal
 * entity. Asking "which company?" on every screen would be both tedious and
 * dangerous: the cost of issuing an invoice from the wrong entity is a document
 * a customer acts on.
 *
 * So: one store, one selector — the compact `CompanySelector` dropdown at the
 * top of each company-scoped screen — and every invoicing query keyed by the
 * active company. This is the single source of that fact; nothing else in the
 * app should hold a second copy, and there is deliberately no selection screen
 * and no top-bar control.
 *
 * ## Why the id is persisted per account
 *
 * The app supports several saved accounts in one browser. A globally-keyed
 * active company would leak across them: sign in as a different person and you
 * would inherit the previous user's entity. The key is therefore suffixed with
 * the user id.
 *
 * ## Why only the id is stored
 *
 * The company NAME and logo come from the API, never from storage. A
 * cached name goes stale the moment somebody renames the company, and a stale
 * legal name on an invoice header is exactly the kind of wrong this module
 * cannot afford. Storage holds an id; the rest is fetched.
 */

const storageKeyFor = (userId: string | null): string =>
  userId ? `${STORAGE_KEYS.ACTIVE_COMPANY}.${userId}` : STORAGE_KEYS.ACTIVE_COMPANY;

function read(userId: string | null): string | null {
  try {
    return window.localStorage.getItem(storageKeyFor(userId));
  } catch (error) {
    // Private mode, blocked site data, or a browser that refuses storage. The
    // store still works for the session; it just does not survive a reload.
    logger.debug('Active company could not be read from storage', error);
    return null;
  }
}

function write(userId: string | null, companyId: string | null): void {
  try {
    const key = storageKeyFor(userId);
    if (companyId) window.localStorage.setItem(key, companyId);
    else window.localStorage.removeItem(key);
  } catch (error) {
    logger.debug('Active company could not be saved to storage', error);
  }
}

interface CompanyState {
  /**
   * The active entity's id, or null before one has been chosen or restored.
   *
   * Null is a real state, not a loading artefact: an account assigned to no
   * company has no entity to operate as, and the invoicing screens say so
   * rather than guessing one.
   */
  activeCompanyId: string | null;
  /** The account these preferences belong to; see the per-account note above. */
  ownerUserId: string | null;

  setActiveCompany: (companyId: string | null) => void;
  /**
   * Bind the store to an account and restore its saved entity.
   *
   * `available` is the set the account may actually reach, and it must be a
   * KNOWN one. A saved id that is no longer in it — access revoked, company
   * deleted — is discarded rather than kept, because operating as an entity you
   * cannot reach produces a screenful of 403s instead of an honest "pick a
   * company".
   *
   * That makes this destructive, which is why the caller must only invoke it
   * after a SUCCESSFUL read of the reachable set. Calling it with `[]` because
   * a request failed erases the user's saved choice over a network blip — see
   * the note in `useActiveCompany`.
   */
  hydrate: (userId: string | null, available: readonly string[]) => void;
  clear: () => void;
}

export const useCompanyStore = create<CompanyState>()(
  subscribeWithSelector((set, get) => ({
    activeCompanyId: null,
    ownerUserId: null,

    setActiveCompany: (companyId) => {
      write(get().ownerUserId, companyId);
      set({ activeCompanyId: companyId });
    },

    hydrate: (userId, available) => {
      const saved = read(userId);
      const valid = saved && available.includes(saved) ? saved : null;
      // Fall back to the account's first reachable company rather than leaving
      // the user on an empty screen with a selector they have to discover.
      const next = valid ?? available[0] ?? null;
      if (next !== saved) write(userId, next);
      set({ ownerUserId: userId, activeCompanyId: next });
    },

    clear: () => set({ activeCompanyId: null, ownerUserId: null }),
  })),
);

/** Read the active company outside React (API layer, query-key builders). */
export const getActiveCompanyId = (): string | null => useCompanyStore.getState().activeCompanyId;
